//#region Asset Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import 'devextreme/ui/button'
import 'devextreme/ui/gallery'
import 'devextreme/ui/load_indicator'

//#endregion

import { UserSessionService } from '../../../services/user-session'
import { CommonHelper, CorrelationType } from '../../../helpers/common-helper'
import { EventsNames } from '../../../helpers/events-names'
import { TemplateHelper, ConfigHelper, LocalizationHelper, TypeHelper } from '../../../helpers'
import { SchemaModel } from '../../../modules/api/api.models'
import { OpSchema } from '../../../models/custom'
import { Enums } from '../../../models/common'
import { IMRBaseControlDirectiveController } from '../base'

interface GalleryScope extends ng.IScope {
	schemas: any[]
	opSchemas: OpSchema[]
	isDxGallery: boolean
	schemaGalleryOptions: DevExpress.ui.dxGalleryOptions
	slickOptions: any
	isLoaded: boolean
	id: number
	disableRefresh: boolean
	referenceType: Enums.ReferenceType
	correlationType: CorrelationType
	eventNameForSchemaChanged?: string

	lastSlide: number
	selectedSlide: number

	isFullScreen: boolean
	resizeButtonOptions: DevExpress.ui.dxButtonOptions
}

export class GalleryDirectiveController extends IMRBaseControlDirectiveController {
	constructor(private readonly $scope: GalleryScope, private readonly userSession: UserSessionService) {
		super()

		this.setEvents()
		this.setOptions()
	}

	//#region Events

	private setEvents(): void {
		this.$scope.$watchCollection('schemas', (value: any[]) => {
			this.$scope.isLoaded = false
			setTimeout(() => {
				this.$scope.isLoaded = true
			}, 0)
		})

		//update slick on any resize (fillScreen etc)
		this.$scope.$watch(
			() => {
				return angular.element('.schemas' + this.$scope.id).width() + angular.element('.schemas' + this.$scope.id).height()
			},
			(val) => {
				this.slickUpdate()
			}
		)

		this.$scope.$on(EventsNames.DASHBOART_GRID_ROW_SELECTED, (event: ng.IAngularEvent, ...args: any[]) => {
			if (_.size(args) > 1) {
				const referenceValues: number[] = args[0] != null ? args[0] : []
				const referenceType: Enums.ReferenceType = args[1]
				const correlationType: CorrelationType = this.$scope.correlationType

				if (_.size(referenceValues) > 0) {
					if (correlationType == CorrelationType.None) {
						return
					}

					if (correlationType == CorrelationType.Local) {
						const value: number = referenceValues[0]
						let index: number = null

						_.forEach(this.$scope.schemas, (val: SchemaModel, i: number) => {
							if (value == val.idObject && referenceType == val.referenceType) index = i
						})

						if (index != null) {
							this.$scope.slickOptions.method.slickGoTo(index)
						}
					}

					if (correlationType == CorrelationType.Server) {
						const value: number = referenceValues[0]

						if (referenceType != null && value != null) {
							this.getSchemas(referenceType, value)
						}
					}
				}

				if (_.size(referenceValues) == 0 && correlationType == CorrelationType.Server) {
					this.$scope.schemas = [{ schema: null }] // ??? not as model. to fix
					this.$scope.$root.$broadcast('schemasLoaded', this.$scope.schemas)
				}
			}
		})

		this.$scope.$on(EventsNames.DASHBOARD_MAP_MARKER_CLICKED, (event: ng.IAngularEvent, ...args: any[]) => {
			const selectedMarker: any = args[0] //OpMarker ?
			const referenceType: Enums.ReferenceType = args[1]
			const correlationType: CorrelationType = this.$scope.correlationType

			if (selectedMarker != null) {
				if (correlationType == CorrelationType.None) {
					return
				}

				if (correlationType == CorrelationType.Local) {
					let index: number = null

					this.$scope.schemas.forEach((val: any, i: number) => {
						if (selectedMarker.id == val.idObject && referenceType == val.referenceType) {
							index = i
						}
					})

					if (index != null) {
						this.$scope.slickOptions.method.slickGoTo(index)
					}
				}

				if (correlationType == CorrelationType.Server) {
					if (referenceType != null && selectedMarker.id != null) {
						this.getSchemas(referenceType, selectedMarker.id)
					}
				}
			}
		})
	}

	//#endregion

	//#region Options

	private setOptions(): void {
		this.$scope.id = new Date().getTime()
		this.$scope.isDxGallery = window['config']['GALLERY_TYPE'] == 'dx-gallery'

		this.$scope.schemaGalleryOptions = {
			height: '100%',
			width: '100%',
			items: this.$scope.schemas,
		}

		this.$scope.resizeButtonOptions = {
			template: TemplateHelper.getIcon(ConfigHelper.Icon.FULLSCREEN, this.userSession),
			hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Fullscreen),
			onClick: (e: any) => {
				this.resizeSchema(this.$scope)
			},
		}

		if (!this.$scope.eventNameForSchemaChanged) {
			this.$scope.eventNameForSchemaChanged = TypeHelper.emptyString
		}

		this.$scope.lastSlide = 0
		this.$scope.$root.$broadcast(EventsNames.SCHEMA_CHANGED + this.$scope.eventNameForSchemaChanged, this.$scope.schemas[0])

		this.$scope.slickOptions = {
			autoplay: false,
			enabled: true,
			dots: true,
			method: {},
			event: {
				afterChange: (e, slick) => {
					if (this.$scope.lastSlide != slick.currentSlide) {
						this.$scope.$root.$broadcast(EventsNames.SCHEMA_CHANGED + this.$scope.eventNameForSchemaChanged, this.$scope.schemas[slick.currentSlide])
						this.$scope.lastSlide = slick.currentSlide
					}
					this.$scope.selectedSlide = slick.currentSlide
					this.$scope.$root.$broadcast(EventsNames.DASHBOARD_SLIDER_SLICKED, this.$scope.schemas[slick.currentSlide])
				},
			},
			nextArrow: '<div class="slick-next"><span class="fa fa-angle-right"></span><span class="sr-only">Next</span></div>',
			prevArrow: '<div class="slick-prev"><span class="fa fa-angle-left"></span><span class="sr-only">Previous</span></div>',
		}
	}

	//#endregion

	private getSchemas(referenceType: Enums.ReferenceType, id: number): void {
		let promise: JQueryPromise<OpSchema[]> = null

		switch (referenceType) {
			case Enums.ReferenceType.IdLocation:
				promise = this.userSession.getDataProvider().Location.getLocationSchemas(id)
				break
			case Enums.ReferenceType.IdMeter:
				promise = this.userSession.getDataProvider().Meter.getMeterSchemas(id)
				break
			case Enums.ReferenceType.SerialNbr:
				promise = this.userSession.getDataProvider().Device.getDeviceSchemas(id)
				break
		}

		if (promise != null) {
			this.$scope.isLoaded = false

			promise.then((schemas: OpSchema[]) => {
				if (_.size(schemas) > 0) {
					this.$scope.schemas = _.map(schemas, (schema: OpSchema) => CommonHelper.opSchemaToSchemaModel(schema, this.userSession))
				} else {
					this.$scope.schemas = [{ schema: null }] // ??? not as model. to fix
				}

				this.$scope.$root.$broadcast('schemasLoaded', this.$scope.schemas)
				this.$scope.isLoaded = true
			})
		}
	}

	private slickUpdate(): void {
		if (!this.$scope.disableRefresh) {
			this.$scope.isLoaded = false
			this.$scope.slickOptions.initialSlide = this.$scope.selectedSlide

			setTimeout(() => {
				this.$scope.isLoaded = true
			})
		}
	}

	private resizeSchema($scope: GalleryScope): void {
		this.$scope.isFullScreen = !$scope.isFullScreen
	}
}
